import { DEFAULT_WORK_ITEM_PRIORITY, type WorkItem } from '@deskworks/shared-types';
import { Link } from '@tanstack/react-router';
import { ArrowDown, ArrowUp, Bot, CornerDownRight, Link2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { RelativeTime } from '@/components/relative-time';
import { WorkItemStatusBadge } from '@/components/status-badge';
import { t } from '@/lib/i18n';
import { cn } from '@/lib/utils';

export function WorkItemRow({
  item,
  workspaceId,
  showDesk = true,
  depth = 0,
  isChild,
  trailing,
  className,
}: {
  item: WorkItem;
  workspaceId: string;
  showDesk?: boolean;
  depth?: number;
  /** Shows a "child of" marker even if the parent isn't displayed. */
  isChild?: boolean;
  trailing?: ReactNode;
  className?: string;
}) {
  return (
    <Link
      to="/w/$workspaceId/work-items/$workItemId"
      params={{ workspaceId, workItemId: item.id }}
      className={cn('group flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-muted/50', className)}
      style={depth ? { paddingLeft: `${1 + depth * 1.5}rem` } : undefined}
    >
      {(depth > 0 || isChild) && <CornerDownRight className="size-3.5 shrink-0 text-muted-foreground" aria-label={t('Sub-item')} />}
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm font-medium group-hover:text-primary">{item.title}</span>
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-xs text-muted-foreground">
          {item.priority !== DEFAULT_WORK_ITEM_PRIORITY && (
            <span
              className={cn('inline-flex items-center gap-0.5 font-medium tabular-nums', item.priority > DEFAULT_WORK_ITEM_PRIORITY && 'text-orange-700 dark:text-orange-400')}
              title={t('Priority {n}', { n: item.priority })}
            >
              {item.priority > DEFAULT_WORK_ITEM_PRIORITY ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" />}
              {item.priority}
            </span>
          )}
          {showDesk && item.deskName && <span className="truncate">{item.deskName}</span>}
          {item.assignedAgentName && (
            <span className="inline-flex items-center gap-1">
              <Bot className="size-3" />
              {item.assignedAgentName}
            </span>
          )}
          {item.externalReference && (
            <span className="inline-flex items-center gap-1 font-mono text-[11px]">
              <Link2 className="size-3" />
              {item.externalReference.type}:{item.externalReference.id}
            </span>
          )}
          {item.status === 'waiting' && item.waitingReason && (
            <span className="truncate text-amber-700 dark:text-amber-400">{item.waitingReason}</span>
          )}
          {item.status === 'blocked' && item.waitingReason && (
            <span className="truncate text-red-700 dark:text-red-400">{item.waitingReason}</span>
          )}
        </div>
      </div>
      <div className={cn('flex shrink-0 items-center gap-3')}>
        <WorkItemStatusBadge status={item.status} paused={item.paused} />
        <RelativeTime value={item.updatedAt} className="hidden w-24 text-right text-xs text-muted-foreground sm:block" />
        {trailing}
      </div>
    </Link>
  );
}

export function WorkItemList({
  items,
  workspaceId,
  showDesk = true,
}: {
  items: WorkItem[];
  workspaceId: string;
  showDesk?: boolean;
}) {
  return (
    <div className="-mx-5 -mb-5 divide-y border-t">
      {items.map((it) => (
        <WorkItemRow key={it.id} item={it} workspaceId={workspaceId} showDesk={showDesk} isChild={!!it.parentWorkItemId} />
      ))}
    </div>
  );
}
